import { useCallback, useLayoutEffect, useRef } from 'react';
import {
  loadReservationBoardFormPrefs,
  reconcileReservationBoardResourceSelection,
  resolveReservationBoardPrefResourceNames,
  saveReservationBoardFormPrefs,
} from '../utils/reservationBoardFormPrefs.util';
import { rsResourceNamesEqual } from '../utils/reservationBoardCalendar.util';

type FormPrefsSlice = {
  displayName: string;
  notes: string;
  selectedResourceNames: string[];
  setDisplayName: (value: string) => void;
  setNotes: (value: string) => void;
  setSelectedResourceNames: (value: string[] | ((prev: string[]) => string[])) => void;
};

/**
 * 共有予約表フォームの localStorage prefs（hydrate + persist）。
 */
export function useRsBoardFormPrefs(
  boardId: number | undefined,
  boardResourceNames: string[] | undefined,
  form: FormPrefsSlice
) {
  const boardResourceKey = boardResourceNames?.join('\u0001') ?? '';
  const hydratedBoardIdRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!boardId || !boardResourceNames) return;

    if (hydratedBoardIdRef.current !== boardId) {
      hydratedBoardIdRef.current = boardId;
      const prefs = loadReservationBoardFormPrefs(boardId);
      if (prefs) {
        form.setDisplayName(prefs.displayName ?? '');
        form.setNotes(prefs.notes ?? '');
        const fromPrefs = resolveReservationBoardPrefResourceNames(
          prefs.resourceNames ?? [],
          boardResourceNames
        );
        if (fromPrefs.length > 0) {
          form.setSelectedResourceNames(fromPrefs);
          return;
        }
      }
      form.setSelectedResourceNames(
        boardResourceNames.length > 0 ? [boardResourceNames[0]] : []
      );
      return;
    }

    form.setSelectedResourceNames((prev) => {
      const next = reconcileReservationBoardResourceSelection(prev, boardResourceNames);
      return rsResourceNamesEqual(prev, next) ? prev : next;
    });
    // board オブジェクト参照は refetch で変わるため、id / resource key のみ依存にする
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setters は安定、上記キー変化時の描画値を使う
  }, [boardId, boardResourceKey]);

  const persistFormPrefs = useCallback(
    (overrides?: Partial<{ displayName: string; resourceNames: string[]; notes: string }>) => {
      if (!boardId) return;
      saveReservationBoardFormPrefs(boardId, {
        displayName: (overrides?.displayName ?? form.displayName).trim(),
        resourceNames: overrides?.resourceNames ?? form.selectedResourceNames,
        notes: (overrides?.notes ?? form.notes).trim(),
      });
    },
    [boardId, form.displayName, form.selectedResourceNames, form.notes]
  );

  return { persistFormPrefs };
}
